/**
 * Événements de conversion poussés dans `dataLayer` (Google Tag Manager),
 * séparés le 14/10/2026 (point A.1 du rapport
 * ~/stock-co2-audit-rapports/plateforme/google-ads-optimisation-2026-10-14.md,
 * validé par Guillaume le 14/10/2026).
 *
 * Jusque-là, TOUT formulaire envoyait `form_submit_lead`, y compris le
 * simulateur des porteurs de projet (/forestiers, /agricoles) : Google Ads
 * apprenait à trouver des propriétaires et des agriculteurs, alors que seules
 * les entreprises clientes (contributeurs) comptent. Désormais :
 *
 *   - `demande_entreprise` : demande d'une entreprise cliente potentielle
 *     (contact, démonstration, devis, questionnaire d'orientation). C'est la
 *     seule conversion principale à déclarer dans Google Ads.
 *   - `form_submit_lead` : ENCORE envoyé, en plus, par le formulaire de
 *     contact (démonstration comprise) et la demande de devis, et par eux
 *     seuls. Dans GTM (GTM-PCGN7DSK, vérifié le 14/10/2026), la balise
 *     « Conversion Lead - Stock CO2 » (AW-769133345) se déclenche sur cet
 *     événement et alimente l'action Google Ads « Envoi de formulaire de
 *     lead » : on ne change rien côté GTM. Le questionnaire d'orientation ne
 *     l'envoyait pas et ne l'envoie toujours pas (il ne compte qu'une fois
 *     GTM passé à `demande_entreprise`).
 *   - `simulation_porteur` (simulateurs des forestiers et des agriculteurs)
 *     et `proposition_porteur` (formulaire « Proposer un projet ») : aucune
 *     balise Google Ads ne les écoute. Jamais `form_submit_lead`, jamais
 *     `demande_entreprise`.
 *
 * Paramètres : `form_name` (identifiant FIXE du formulaire, typé ci-dessous)
 * et `type_demandeur`. Aucune donnée saisie par le visiteur — ni e-mail, ni
 * nom, ni réponse — ne peut passer : les fonctions n'acceptent que les
 * identifiants listés ici.
 *
 * Les poussées ne partent que si `dataLayer` existe déjà (créé par le
 * bandeau, src/components/CookieConsent.tsx) ; GTM n'est chargé qu'après
 * consentement (src/components/AnalyticsGate.tsx).
 */

export const EVENEMENT_DEMANDE_ENTREPRISE = 'demande_entreprise'
export const EVENEMENT_SIMULATION_PORTEUR = 'simulation_porteur'
export const EVENEMENT_PROPOSITION_PORTEUR = 'proposition_porteur'
/** Ancien événement unique, écouté par la balise de conversion Google Ads de GTM : contact et devis seulement. */
export const EVENEMENT_HISTORIQUE = 'form_submit_lead'

/** Formulaires d'entreprise (form_name). */
export const FORMULAIRES_ENTREPRISE = ['contact', 'demande-demo', 'demande-devis', 'questionnaire-orientation'] as const
/** Parcours de porteur de projet (form_name). */
export const FORMULAIRES_PORTEUR = ['simulateur-foret', 'simulateur-agriculture', 'proposer-projet'] as const

/** Les seuls formulaires qui envoient encore `form_submit_lead` (balise « Conversion Lead - Stock CO2 »). */
export const FORMULAIRES_HISTORIQUES: readonly FormulaireEntreprise[] = ['contact', 'demande-demo', 'demande-devis']

export type FormulaireEntreprise = (typeof FORMULAIRES_ENTREPRISE)[number]
export type FormulairePorteur = (typeof FORMULAIRES_PORTEUR)[number]

export interface EvenementConversion {
  event: string
  form_name: FormulaireEntreprise | FormulairePorteur
  type_demandeur: 'entreprise' | 'porteur'
}

/** Ce que pousse une demande d'entreprise : la conversion, puis l'ancien événement (contact et devis seulement). */
export function evenementsDemandeEntreprise(formulaire: FormulaireEntreprise): EvenementConversion[] {
  const e: EvenementConversion[] = [{ event: EVENEMENT_DEMANDE_ENTREPRISE, form_name: formulaire, type_demandeur: 'entreprise' }]
  if (FORMULAIRES_HISTORIQUES.includes(formulaire)) e.push({ event: EVENEMENT_HISTORIQUE, form_name: formulaire, type_demandeur: 'entreprise' })
  return e
}

/** Ce que pousse un parcours de porteur de projet : un seul événement, jamais une conversion entreprise. */
export function evenementsSimulationPorteur(formulaire: FormulairePorteur): EvenementConversion[] {
  const event = formulaire === 'proposer-projet' ? EVENEMENT_PROPOSITION_PORTEUR : EVENEMENT_SIMULATION_PORTEUR
  return [{ event, form_name: formulaire, type_demandeur: 'porteur' }]
}

function pousser(evenements: EvenementConversion[]): void {
  if (typeof window === 'undefined') return
  const dl = (window as unknown as { dataLayer?: unknown[] }).dataLayer
  if (!Array.isArray(dl)) return
  for (const e of evenements) dl.push({ ...e })
}

/** À appeler après l'enregistrement réussi d'une demande d'entreprise. */
export function signalerDemandeEntreprise(formulaire: FormulaireEntreprise): void {
  pousser(evenementsDemandeEntreprise(formulaire))
}

/** À appeler après l'enregistrement réussi d'une demande de porteur de projet. */
export function signalerSimulationPorteur(formulaire: FormulairePorteur): void {
  pousser(evenementsSimulationPorteur(formulaire))
}

/** Formulaire de contact : « demande-demo » quand le sujet choisi est la démonstration. */
export function formulaireContact(sujet: string | null | undefined): FormulaireEntreprise {
  return sujet === 'demonstration' ? 'demande-demo' : 'contact'
}
